import { useEffect, useState, useContext } from 'react'
import { useAPI } from 'src/utils/api'
import {
  Box,
  Tabs,
  Tab,
  List,
  ListItemButton,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Button
} from '@mui/material'
import { Icon } from '@iconify/react'
import { AuthContext } from 'src/context/AuthContext'
import { useAuth } from 'src/hooks/useAuth'
import { styled } from '@mui/material/styles'
import Link from 'next/link'
import authConfig from 'src/configs/auth'
import { rememberMe } from 'src/utils/rememberMe'
import { getImageSrc } from 'src/utils/imageSrc'

const LinkStyled = styled(Link)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  textDecoration: 'none',
  marginRight: theme.spacing(8)
}))

const SideBarContent = ({ toggleDrawer }) => {
  const [tabValue, setTabValue] = useState(0)
  const { logoUrlFromSettings } = useAuth(AuthContext)
  const api = useAPI()
  const {
    clientData,
    setClientData,
    setLoggedInClientId,
    selectedClientId,
    setSelectedClientId,
    updateUserClientFavouriteStatus
  } = useContext(AuthContext)
  const [searchQuery, setSearchQuery] = useState('')
  const [openSearch, setOpenSearch] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [clientsState, setClientsState] = useState([])

  useEffect(() => {
    if (clientData?.clients) {
      setClientsState(clientData.clients)
    }
  }, [clientData])

  const handleTabChange = (event, newValue) => setTabValue(newValue)

  const formatLastVisited = dateString => {
    if (!dateString) {
      return null
    }
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = Math.abs(now - date)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    if (diffDays === 1) {
      return 'Today'
    }
    if (diffDays === 2) {
      return 'Yesterday'
    }
    if (diffDays < 7) {
      return `${diffDays} days ago`
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const handleClientSelection = async clientId => {
    const selectedClient = clientsState.find(client => client.clientId === clientId)
    if (!selectedClient) {
      return
    }

    // With a single client there's nowhere else to switch to - avoid a pointless
    // client-login round-trip and page reload.
    if (clientsState.length === 1) {
      return
    }

    try {
      if (toggleDrawer) {
        toggleDrawer(false)()
      }
      setIsLoading(true)

      // Routed through the shared `api` client (not raw axios) so that if
      // access_token has already expired - its cookie is gone by then, the
      // backend 400s "token missing" - the response interceptor's silent
      // refresh-and-retry (see src/utils/api.js) kicks in automatically instead
      // of this call just failing outright. withCredentials and the
      // X-CSRF-Token header are both already handled by that shared client.
      await api.post(authConfig.clientLoginEndpoint, {
        clientId: selectedClient.clientId,
        email: clientData?.email,
        is_favourite: selectedClient.is_favourite,
        // Keep the login's Remember Me choice for the new client's session cookies.
        rememberMe: rememberMe.isRemembered()
      })

      // Success response carries no token - the new access_token/refresh_token
      // (HttpOnly) and rotated csrf_token cookies are set directly.

      setLoggedInClientId(selectedClient.clientId)
      setSelectedClientId(selectedClient.clientId)
      sessionStorage.setItem('switchingToClient', selectedClient.clientName)

      window.location.href = '/'
    } catch (error) {
      // Client-switch failure is a no-op in the UI - the user stays on the current
      // client - but log the reason so a rejected switch is diagnosable from the console.
      // eslint-disable-next-line no-console
      console.error('Client switch failed:', error?.response?.status, error?.response?.data)
    } finally {
      setIsLoading(false)
    }
  }

  const handleFavouriteToggle = async clientId => {
    const clickedClient = clientsState.find(c => c.clientId === clientId)
    if (!clickedClient) {
      return
    }

    const newFavValue = !clickedClient.is_favourite

    // With a single client there's nothing else to become favourite, so
    // unfavoriting it is a no-op the backend rejects anyway - block it here
    // instead of silently drifting out of sync with the persisted value.
    if (!newFavValue && clientsState.length === 1) {
      return
    }

    // Update local state: only one client can be favorite at a time
    const updatedClients = clientsState.map(client =>
      client.clientId === clientId ? { ...client, is_favourite: newFavValue } : { ...client, is_favourite: false }
    )
    setClientsState(updatedClients)

    // Call the API to update favorite status
    if (updateUserClientFavouriteStatus) {
      try {
        await updateUserClientFavouriteStatus(clientId, newFavValue)

        // Update the clientData in AuthContext to persist the change
        if (setClientData && clientData) {
          setClientData({
            ...clientData,
            clients: updatedClients
          })
        }
      } catch {
        // If API call fails, revert the local state
        setClientsState(clientData?.clients || [])
      }
    }
  }

  const renderClientData = (clients = []) => {
    const filtered = clients.filter(client =>
      client.clientName.toLowerCase().includes(searchQuery.toLowerCase().trim())
    )

    if (filtered.length === 0) {
      return (
        <Box textAlign='center' py={6}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              backgroundColor: '#f0f4fa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 1.5
            }}
          >
            <Icon icon='mdi:account-search-outline' style={{ fontSize: 26, color: '#94a3b8' }} />
          </Box>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#64748b', mb: 0.5 }}>No clients found</Typography>
          <Typography sx={{ fontSize: 12, color: '#b0bac8' }}>Try a different search term</Typography>
        </Box>
      )
    }

    return filtered.map(data => {
      const isSelected = data.clientId === selectedClientId
      const isFavourite = data.is_favourite === true
      const isOnlyClient = clientsState.length === 1
      const lastVisitedText = formatLastVisited(data.last_visited_at)
      const firstLetter = data.clientName?.charAt(0).toUpperCase() || '?'

      let statusDisplay
      if (isSelected) {
        statusDisplay = (
          <Box display='flex' alignItems='center' gap={0.6}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#52d78f',
                boxShadow: '0 0 0 2.5px rgba(82,215,143,0.25)',
                flexShrink: 0
              }}
            />
            <Typography sx={{ fontSize: 11.5, color: '#52d78f', fontWeight: 600, letterSpacing: 0.3 }}>
              Active now
            </Typography>
          </Box>
        )
      } else if (data.last_visited_at === null) {
        statusDisplay = (
          <Typography sx={{ fontSize: 11.5, color: '#c0c7d0', fontStyle: 'italic' }}>Never visited</Typography>
        )
      } else {
        statusDisplay = <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{lastVisitedText}</Typography>
      }

      return (
        <ListItemButton
          key={data.clientId}
          data-testid={`client-row-${data.clientId}`}
          onClick={() => handleClientSelection(data.clientId)}
          disabled={isLoading || isOnlyClient}
          disableRipple={isOnlyClient}
          sx={{
            mb: 1,
            borderRadius: '10px',
            py: 2.75,
            px: 1.75,
            justifyContent: 'space-between',
            cursor: isOnlyClient ? 'default' : 'pointer',
            backgroundColor: isSelected ? '#1b2a4a' : '#fff',
            border: '1px solid',
            borderColor: isSelected ? '#253d68' : '#eaedf1',
            boxShadow: isSelected ? '0 2px 10px rgba(27,42,74,0.28)' : '0 1px 2px rgba(0,0,0,0.05)',
            '&:hover': isOnlyClient
              ? {}
              : {
                  backgroundColor: isSelected ? '#22345a' : '#f4f7fd',
                  borderColor: isSelected ? '#2e4a7c' : '#c5d4e8',
                  boxShadow: isSelected ? '0 4px 14px rgba(27,42,74,0.36)' : '0 2px 6px rgba(0,0,0,0.08)',
                  transform: 'translateY(-1px)'
                },
            transition: 'all 0.15s ease',
            '&.Mui-disabled': {
              opacity: 1,
              backgroundColor: isSelected ? '#1b2a4a' : '#fff',
              borderColor: isSelected ? '#253d68' : '#eaedf1'
            }
          }}
        >
          <Box display='flex' alignItems='center' flex={1} minWidth={0}>
            <Box sx={{ flexShrink: 0 }}>
              {data.image ? (
                <img
                  src={getImageSrc(data.image)}
                  alt=''
                  width={38}
                  height={38}
                  loading='lazy'
                  style={{ borderRadius: '50%', display: 'block', objectFit: 'cover' }}
                />
              ) : (
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '50%',
                    background: isSelected
                      ? 'rgba(255,255,255,0.14)'
                      : 'linear-gradient(140deg, #3d82d4 0%, #63a5ef 100%)',
                    border: isSelected ? '1.5px solid rgba(255,255,255,0.22)' : '1.5px solid rgba(61,130,212,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 15,
                    letterSpacing: 0,
                    fontFamily: 'inherit'
                  }}
                >
                  {firstLetter}
                </Box>
              )}
            </Box>
            <Box sx={{ ml: 1.5, flex: 1, minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  letterSpacing: '1px',
                  lineHeight: 1.35,
                  color: isSelected ? '#fff' : '#1e293b'
                }}
              >
                {data.clientName}
              </Typography>
              <Box mt={0.3}>{statusDisplay}</Box>
            </Box>
          </Box>

          <Box display='flex' alignItems='center' gap={0.25} sx={{ ml: 0.5, flexShrink: 0 }}>
            <IconButton
              size='small'
              data-testid={`favourite-toggle-${data.clientId}`}
              disabled={isFavourite && isOnlyClient}
              onClick={e => {
                e.stopPropagation()
                handleFavouriteToggle(data.clientId)
              }}
              sx={{
                p: 0.5,
                opacity: isFavourite ? 1 : 0.6,
                '&:hover': { opacity: 1, background: 'transparent' },
                '&.Mui-disabled': { opacity: isFavourite ? 1 : 0.6 }
              }}
            >
              <Icon
                icon={isFavourite ? 'mdi:star' : 'mdi:star-outline'}
                style={{ color: isFavourite ? '#f5a623' : isSelected ? '#8dafd4' : '#a0aec0', fontSize: 17 }}
              />
            </IconButton>
            <Icon
              icon='mdi:chevron-right'
              style={{ fontSize: 17, color: isSelected ? 'rgba(255,255,255,0.5)' : '#cbd5e1' }}
            />
          </Box>
        </ListItemButton>
      )
    })
  }

  return (
    <Box sx={{ width: 340, height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 2.5, pb: 0, backgroundColor: '#f6f8f8', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <LinkStyled href='/'>
            <img
              src={logoUrlFromSettings ? logoUrlFromSettings : '/images/cxpify_PNG.png'}
              alt='TotientCXP Logo'
              height={30}
            />
          </LinkStyled>
          <IconButton onClick={toggleDrawer(false)} size='small' sx={{ color: '#666' }}>
            <Icon icon='mdi:close' />
          </IconButton>
        </Box>

        {/* Tabs + Search overlay */}
        <Box sx={{ position: 'relative' }}>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            sx={{ visibility: openSearch ? 'hidden' : 'visible' }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                minHeight: 40,
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 13, minHeight: 40, px: 0.5 },
                '& .Mui-selected': { color: '#337ab7' },
                '& .MuiTabs-indicator': { backgroundColor: '#337ab7', height: 2.5, borderRadius: 2 }
              }}
            >
              <Tab label='All Clients' />
            </Tabs>
            <IconButton size='small' onClick={() => setOpenSearch(true)} sx={{ color: '#555', mb: 0.5 }}>
              <Icon icon='mdi:magnify' />
            </IconButton>
          </Box>

          {openSearch && (
            <Box
              display='flex'
              alignItems='center'
              gap={1}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: '#f6f8f8',
                zIndex: 1
              }}
            >
              <TextField
                size='small'
                fullWidth
                autoFocus
                value={searchQuery}
                placeholder='Search clients...'
                onChange={e => setSearchQuery(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root fieldset': { borderColor: 'transparent' },
                  '& .Mui-focused fieldset': { borderColor: '#3c6bcc' },
                  '& .MuiOutlinedInput-root': { backgroundColor: '#eef1f6', borderRadius: 2, fontSize: 13 }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Icon icon='mdi:magnify' style={{ color: '#888', fontSize: 18 }} />
                    </InputAdornment>
                  )
                }}
              />
              <Button
                size='small'
                onClick={() => {
                  setSearchQuery('')
                  setOpenSearch(false)
                }}
                sx={{
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  color: '#337ab7',
                  fontWeight: 600,
                  flexShrink: 0,
                  px: 0
                }}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Subtext */}
      <Typography
        sx={{
          fontSize: 11,
          color: '#aaa',
          letterSpacing: 0.2,
          px: 2.5,
          pt: 1.5,
          pb: 0.5,
          visibility: openSearch ? 'visible' : 'hidden'
        }}
      >
        Search by client name
      </Typography>

      {/* Client list */}
      {tabValue === 0 && (
        <>
          {!openSearch && clientsState.length > 0 && (
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 500,
                color: '#b0bac8',
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                px: 2.5,
                pt: 0.5,
                pb: 0.75
              }}
            >
              {clientsState.length} {clientsState.length === 1 ? 'client' : 'clients'}
            </Typography>
          )}
          <List sx={{ px: 1.5, pt: 0.5, pb: 2 }}>{renderClientData(clientsState)}</List>
        </>
      )}
    </Box>
  )
}

export default SideBarContent
